import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import useEmblaCarousel from "embla-carousel-react";
import type { EmblaCarouselType } from "embla-carousel";
import { twMerge } from "tailwind-merge";
import { type RtfmAppState } from "../hooks/useRtfmAppState.js";
import { DEFAULT_SESSION_TOTAL_SECONDS, RtfmDerivedState, RtfmLifecycle } from "@/hooks/useRtfmLifecycle.js";
import { ButtonSimple } from "./RtfmDemoApp.js";
import { useDotButton } from "./EmblaCarouselDotButton";
import { ArrowLeftIcon, ArrowRightIcon, InfoCircledIcon } from "@radix-ui/react-icons";

export const CarouselSection = ({
  state,
  derived,
  lifecycle,
  loadScene,
}: {
  state: RtfmAppState;
  derived: RtfmDerivedState;
  lifecycle: RtfmLifecycle;
  loadScene: (worldId: string) => Promise<void>;
}) => {

  const timeSinceStart = DEFAULT_SESSION_TOTAL_SECONDS - lifecycle.sessionRemainingSeconds;
  const tutorialText = (() => {
    if (timeSinceStart <= 20) {
      return "move with the joysticks";
    } else if (timeSinceStart <= 60) {
      return "drag/click scenes to load";
    } else if (timeSinceStart <= 100) {
      return "reset if you get stuck";
    } else if (timeSinceStart <= 140) {
      return "zoom with pinch/scroll"
    } else {
      return "goodbye"
    }
  })();
  
  const viewportNodeRef = useRef<HTMLDivElement | null>(null);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    dragFree: true,
    containScroll: "trimSnaps",
    slidesToScroll: 1,
  });
  const setViewportRef = useCallback(
    (node: HTMLDivElement | null) => {
      viewportNodeRef.current = node;
      emblaRef(node);
    },
    [emblaRef],
  );

  const worldEntries = useMemo(
    () => Object.entries(state.worldsList),
    [state.worldsList],
  );
  const worldIds = useMemo(
    () => worldEntries.map(([id]) => id),
    [worldEntries],
  );

  const { selectedIndex } = useDotButton(emblaApi);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    if (emblaApi.canScrollPrev()) {
      emblaApi.scrollPrev();
    } else {
      const lastIndex = emblaApi.scrollSnapList().length - 1;
      if (lastIndex >= 0) emblaApi.scrollTo(lastIndex);
    }
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    if (emblaApi.canScrollNext()) {
      emblaApi.scrollNext();
    } else {
      emblaApi.scrollTo(0);
    }
  }, [emblaApi]);

  const sceneLocked = state.sceneLoadStatus !== "idle" || !derived.isActive;
  const isLoading = state.sceneLoadStatus === "loading";
  const [scrollProgress, setScrollProgress] = useState(0);
  

  const onKeyDownCapture = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      const viewportNode = viewportNodeRef.current;
      if (!viewportNode) return;
      const target = e.target as Node | null;
      if (!target) return;
      if (!viewportNode.contains(target)) return;
      if (
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight" ||
        e.key === "ArrowUp" ||
        e.key === "ArrowDown"
      ) {
        e.preventDefault();
      }
    },
    [],
  );

  const updateScrollProgress = useCallback((api: EmblaCarouselType) => {
    const progress = api.scrollProgress();
    const clamped = Number.isFinite(progress)
      ? Math.max(0, Math.min(1, progress))
      : 0;
    setScrollProgress(clamped);
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    updateScrollProgress(emblaApi);
    emblaApi.on("reInit", updateScrollProgress);
    emblaApi.on("scroll", updateScrollProgress);
    return () => {
      emblaApi.off("reInit", updateScrollProgress);
      emblaApi.off("scroll", updateScrollProgress);
    };
  }, [emblaApi, updateScrollProgress]);

  useEffect(() => {
    if (!emblaApi) return;
    const maxIndex = worldEntries.length - 1;
    if (maxIndex < 0) return;
    if (selectedIndex > maxIndex) {
      emblaApi.scrollTo(maxIndex, true);
    }
  }, [emblaApi, selectedIndex, worldEntries.length]);

  return (
    <div
      className="w-full h-full flex flex-col gap-2 select-none"
      onKeyDownCapture={onKeyDownCapture}
    >
      <div className="relative w-full h-full border border-primary rounded-lg flex flex-row items-center gap-0 py-1 px-1">

        <ButtonSimple
          aria-label="Previous"
          onClick={onPrevButtonClick}
          className="h-full w-8 left-0 pointer-events-auto opacity-50 hover:opacity-100 cursor-pointer z-50 bg-transparent border-none p-0"
        >
          <ArrowLeftIcon className=" h-6 w-6" />
        </ButtonSimple>

        <div
          className="relative overflow-hidden w-full grow cursor-grab"
          ref={setViewportRef}
          aria-label="World previews"
          role="region"
        >
          {/* gradient that covers edges of the carousel on either side */}
          <div className="flex">
            {worldEntries.map(([id, world], index) => {
              const isCurrentWorld = id === state.currentWorldId;
              const isLoadingWorld =
                isLoading && state.loadingWorldId === id;
              return (
                <div
                  key={id}
                  className="embla__slide box-border flex-[0_0_25%] basis-1/4 md:basis-1/5 p-0.5"
                >
                  <button
                    type="button"
                    className={twMerge(
                      "relative aspect-square h-full w-full overflow-hidden transition-opacity duration-150",
                      "opacity-70 hover:opacity-100 focus-visible:outline-none focus-visible:ring-none rounded-[1em]",
                      "bg-transparent",
                      isCurrentWorld && "opacity-100",
                      isLoadingWorld && "opacity-100",
                    )}
                    onClick={() => {
                      if (!emblaApi) return;
                      const clickAllowed = (emblaApi as any)?.clickAllowed?.() ?? true;
                      if (!clickAllowed) return;
                      if (sceneLocked || isLoading) return;
                      if (id === state.currentWorldId) return;
                      loadScene(id).catch(() => { });
                    }}
                    aria-label={`Load ${world.name}`}
                    tabIndex={-1}
                  >
                    <img
                      src={world.thumbnail}
                      alt={world.name}
                      className="absolute inset-0 p-0.5 rounded-[1em] h-full w-full select-none object-cover object-center cursor-grab"
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
        <ButtonSimple
          aria-label="Next"
          onClick={onNextButtonClick}
          className="h-full w-8 left-0 pointer-events-auto opacity-50 hover:opacity-100 cursor-pointer z-50 bg-transparent border-none p-0"
        >
          <ArrowRightIcon className="h-6 w-6" />
        </ButtonSimple>
      </div>

      {/* {worldEntries.length > 0 && (
        <div className="relative h-px w-full bg-black/30">
          <div
            className="absolute inset-y-0 h-full bg-black transition-all duration-200 ease-out"
            style={{
              width: `${indicatorWidth * 100}%`,
              left: `${indicatorOffset * 100}%`,
            }}
          />
        </div>
      )} */}
      <div className="relative w-full flex flex-row items-center justify-between h-7 border border-primary rounded-lg p-3 text-[0.8em] md:text-[1em]">
        <div className="truncate text-left text-primary">
          {state.currentWorldId
            ? (state.worldsList[state.currentWorldId]?.name ?? "Active level")
            : "No active level"}
        </div>
        <div className={`absolute right-3 w-full flex flex-row justify-end items-center gap-1 text-right text-primary whitespace-nowrap pointer-events-none select-none pl-2 duration-200 ease-in-out transition-all ${!isLoading && tutorialText !== "goodbye" ? "opacity-40 translate-y-0" : "opacity-0 -translate-y-1"}`}>
          <p>{tutorialText}</p>
          {tutorialText && <InfoCircledIcon className="w-3.5 h-3.5" />}
        </div>
        <div className={`text-right text-primary whitespace-nowrap pl-2 duration-200 ease-in-out transition-all ${isLoading && state.loadingWorldId ? "opacity-50 translate-y-0" : "opacity-0 translate-y-1"}`}>
          {`Loading ${state.worldsList[state.loadingWorldId ?? ""]?.name ?? "level"}`}
        </div>
      </div>
    </div>
  );
};
